import { effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { KeycloakEventType, KEYCLOAK_EVENT_SIGNAL } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { KeycloakTokenParsed } from 'keycloak-js';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

import { setSessionParams, authentication, revokeAuthentication } from 'shared/auth/store/auth.actions';
import { AuthBaseService } from 'shared/services/auth-base.service';
import { LoggerService } from 'shared/services/logger.service';

export abstract class KeycloakAuthBaseService extends AuthBaseService {
	private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
	private readonly keycloak = inject(Keycloak);

	// eslint-disable-next-line
	protected constructor(logger: LoggerService, dialog: MatDialog, router: Router, store: Store<{ auth: any }>) {
		super(logger, dialog, router, store);
		effect(() => {
			const keycloakEvent = this.keycloakSignal();
			if (
				keycloakEvent.type === KeycloakEventType.AuthSuccess ||
				keycloakEvent.type === KeycloakEventType.AuthRefreshSuccess
			) {
				this.dispatchSessionPeriod(this.keycloak.refreshTokenParsed);
			}
		});
	}

	public override logout(): void {
		fromPromise(this.keycloak.logout()).subscribe({
			next: () => {
				this.store.dispatch(revokeAuthentication());
			},
			error: (error) => {
				this.logger.log(this.constructor.name, this.logout.name, error);
				this.store.dispatch(revokeAuthentication());
			},
		});
	}

	public override keepSessionAlive(): void {
		this.keycloak.updateToken(-1);
	}

	protected override async handleSessionOnInit(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.keycloak.onReady = (authenticated) => {
				if (authenticated) {
					try {
						this.store.dispatch(authentication());
						this.preload()
							.then(() => resolve())
							.catch(() => reject());
					} catch (error) {
						this.store.dispatch(revokeAuthentication());
						reject(error);
					}
				} else {
					this.store.dispatch(revokeAuthentication());
					reject();
				}
			};
		});
	}

	protected override onNotAuthenticated() {
		this.keycloak.logout();
	}

	protected override getSessionExpirationTime(): number {
		const refreshToken = this.keycloak.refreshTokenParsed;
		if (refreshToken && refreshToken.exp) {
			return new Date(refreshToken.exp * 1000).getTime();
		}
		return 0;
	}

	private dispatchSessionPeriod(refreshToken?: KeycloakTokenParsed) {
		this.store.dispatch(
			setSessionParams({
				payload: {
					sessionPeriod: refreshToken ? refreshToken.exp! - refreshToken.iat! : 0,
				},
			})
		);
	}
}
